/**
 * FmCorridorTrajectoryChart.legend — the legend of the corridor-trajectory
 * figure, the AUTHORITATIVE decode of every drawn series (the plot carries no
 * rate identity tags — visual-QA F1).
 *
 * The line-decode convention is stated ONCE above the rate rows (solid swatch
 * beside the served residual label, dashed swatch beside the served trigger
 * label). The rates then form a real two-column grid: the served delta header
 * is printed once over a right-aligned value column, and each covered rate is
 * ONE row — swatch pair, rate label, value — so the repeated delta stem never
 * returns (visual-QA F6). Below the container breakpoint each rate collapses
 * to one grouped row: swatch + name with the value on its own line at the
 * text indent, and the inter-rate gap grows so a value can never float
 * between rates. Muted rates carry their step of the neutral rate ramp,
 * identical to their stroke (visual-QA F3). A genuinely banded rate (paired
 * published edges — the 2019 print edition) emits its header row plus TWO
 * band rows — residual band and trigger band — because a band is a filled
 * rect with edges, never a line. Coverage refusals close the legend as named
 * rows with real typographic weight.
 */
import React from 'react';
import type {
  FmStoryCorridorRateSeries,
  FmStoryCorridorRefusal,
  FmStoryCorridorTrajectory,
} from './FmCorridorTrajectory.types';

export type CorridorSwatchVariant =
  | 'enforced'
  | 'muted'
  | 'floor'
  | 'selected'
  | 'reference'
  | 'rate-1'
  | 'rate-2'
  | 'rate-3';

/** The legend swatch step for a muted rate — same ramp as its stroke. */
function corridorRateRampVariant(rateIndex: number): CorridorSwatchVariant {
  const step = Math.min(rateIndex, 2) + 1;
  return `rate-${step}` as CorridorSwatchVariant;
}

const LegendSwatch: React.FC<{
  variant: CorridorSwatchVariant;
  dashed?: boolean;
  shape?: 'line' | 'band';
}> = ({ variant, dashed, shape = 'line' }) => (
  <span
    className={[
      'fm-corridor-trajectory__swatch',
      `fm-corridor-trajectory__swatch--${variant}`,
      dashed ? 'fm-corridor-trajectory__swatch--dashed' : '',
      shape === 'band' ? 'fm-corridor-trajectory__swatch--band' : '',
    ]
      .filter(Boolean)
      .join(' ')}
    aria-hidden="true"
  />
);

/** One rate row's swatch pair: its solid residual and dashed trigger marks. */
const LegendSwatchPair: React.FC<{ variant: CorridorSwatchVariant }> = ({ variant }) => (
  <span className="fm-corridor-trajectory__legend-swatch-pair" aria-hidden="true">
    <LegendSwatch variant={variant} />
    <LegendSwatch variant={variant} dashed />
  </span>
);

function isBanded(rate: FmStoryCorridorRateSeries): boolean {
  return Boolean(rate.residualUpper ?? rate.triggerUpper);
}

function rateVariant(rate: FmStoryCorridorRateSeries, rateIndex: number): CorridorSwatchVariant {
  return rate.emphasis === 'enforced' ? 'enforced' : corridorRateRampVariant(rateIndex);
}

/**
 * Group refusals sharing one IDENTICAL served reason, preserving served
 * order (visual-QA round-2 n1): the reason states once, the affected rates
 * list under it. Distinct reasons keep their own rows.
 */
function groupRefusalsByReason(
  refusals: readonly FmStoryCorridorRefusal[],
): Array<{ reason: string; refusals: FmStoryCorridorRefusal[] }> {
  const groups: Array<{ reason: string; refusals: FmStoryCorridorRefusal[] }> = [];
  for (const refusal of refusals) {
    const group = groups.find((candidate) => candidate.reason === refusal.reason);
    if (group) group.refusals.push(refusal);
    else groups.push({ reason: refusal.reason, refusals: [refusal] });
  }
  return groups;
}

/**
 * The line-decode convention, stated once: solid = residual, dashed =
 * trigger, using the SERVED residualLabel/triggerLabel strings (payload /
 * i18n-supplied — no prose is invented here). Rendered only when at least
 * one line-decoded (non-banded) rate draws.
 */
const LegendConvention: React.FC<{ rate: FmStoryCorridorRateSeries; testId: string }> = ({
  rate,
  testId,
}) => (
  <div
    className="fm-corridor-trajectory__legend-convention"
    data-testid={`${testId}-legend-convention`}
  >
    <span className="fm-corridor-trajectory__legend-series">
      <LegendSwatch variant="muted" />
      <span className="fm-corridor-trajectory__legend-series-label">{rate.residualLabel}</span>
    </span>
    <span aria-hidden="true">·</span>
    <span className="fm-corridor-trajectory__legend-series">
      <LegendSwatch variant="muted" dashed />
      <span className="fm-corridor-trajectory__legend-series-label">{rate.triggerLabel}</span>
    </span>
  </div>
);

const LegendRateEntry: React.FC<{
  rate: FmStoryCorridorRateSeries;
  rateIndex: number;
  testId: string;
}> = ({ rate, rateIndex, testId }) => {
  const variant = rateVariant(rate, rateIndex);
  const labelSpan = (
    <span className="fm-corridor-trajectory__legend-label">
      {rate.label}
      {rate.emphasisLabel ? (
        <em className="fm-corridor-trajectory__legend-note"> · {rate.emphasisLabel}</em>
      ) : null}
    </span>
  );
  const delta = rate.deltaLabel ? (
    <span className="fm-corridor-trajectory__legend-delta">{rate.deltaLabel}</span>
  ) : null;
  if (isBanded(rate)) {
    return (
      <div
        className={`fm-corridor-trajectory__legend-rate fm-corridor-trajectory__legend-rate--${rate.emphasis}`}
        data-emphasis={rate.emphasis}
        data-testid={rate.testId ?? `${testId}-rate-${rate.id}`}
      >
        <div
          className={`fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--${rate.emphasis}`}
        >
          {labelSpan}
          {delta}
        </div>
        <div className="fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--grid fm-corridor-trajectory__legend-row--band">
          <LegendSwatch variant={variant} shape="band" />
          <span className="fm-corridor-trajectory__legend-label">{rate.residualLabel}</span>
        </div>
        <div className="fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--grid fm-corridor-trajectory__legend-row--band">
          <LegendSwatch variant={variant} shape="band" dashed />
          <span className="fm-corridor-trajectory__legend-label">{rate.triggerLabel}</span>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--grid fm-corridor-trajectory__legend-row--${rate.emphasis}`}
      data-emphasis={rate.emphasis}
      data-testid={rate.testId ?? `${testId}-rate-${rate.id}`}
    >
      <LegendSwatchPair variant={variant} />
      {labelSpan}
      {delta}
    </div>
  );
};

export const CorridorChartLegend: React.FC<{
  trajectory: FmStoryCorridorTrajectory;
  testId: string;
}> = ({ trajectory, testId }) => {
  const floor = trajectory.statutoryFloor;
  const conventionRate = trajectory.rates.find((rate) => !isBanded(rate));
  const hasDeltaColumn = trajectory.rates.some((rate) => Boolean(rate.deltaLabel));
  return (
    <div className="fm-corridor-trajectory__legend" data-testid={`${testId}-legend`}>
      {trajectory.rates.length === 0 && trajectory.emptyLabel ? (
        // The validator-legal all-refusals case: every rate's cell is a named
        // refusal, so no corridor draws. The plot still shows the served
        // subject and statutory floor; this row says WHY no guidance curve
        // accompanies them instead of leaving a silently thinner chart.
        <div
          className="fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--empty"
          data-testid={`${testId}-empty`}
        >
          <span className="fm-corridor-trajectory__legend-label">{trajectory.emptyLabel}</span>
        </div>
      ) : null}
      {conventionRate ? <LegendConvention rate={conventionRate} testId={testId} /> : null}
      {hasDeltaColumn && trajectory.deltaHeader ? (
        // The served delta stem, printed ONCE over the value column instead of
        // repeated on every rate row (visual-QA F6).
        <div
          className="fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--grid fm-corridor-trajectory__legend-row--delta-header"
          data-testid={`${testId}-delta-header`}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span className="fm-corridor-trajectory__legend-delta fm-corridor-trajectory__legend-delta-header">
            {trajectory.deltaHeader}
          </span>
        </div>
      ) : null}
      {trajectory.rates.map((rate, rateIndex) => (
        <LegendRateEntry key={rate.id} rate={rate} rateIndex={rateIndex} testId={testId} />
      ))}
      <div
        className="fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--grid fm-corridor-trajectory__legend-row--floor"
        data-testid={`${testId}-statutory-floor`}
      >
        <LegendSwatch variant="floor" dashed />
        <span className="fm-corridor-trajectory__legend-label">
          {floor.label}
          {floor.detail ? (
            <em className="fm-corridor-trajectory__legend-note"> · {floor.detail}</em>
          ) : null}
        </span>
        <span className="fm-corridor-trajectory__legend-delta">
          {floor.unavailableLabel ??
            ('valueY' in floor ? `${floor.valueY} ${trajectory.yUnit}` : null)}
        </span>
      </div>
      {(trajectory.trajectories ?? []).map((path) => (
        <div
          key={path.id}
          className={`fm-corridor-trajectory__legend-row fm-corridor-trajectory__legend-row--grid fm-corridor-trajectory__legend-row--${path.role}`}
          data-testid={path.testId ?? `${testId}-path-${path.id}`}
        >
          <LegendSwatch variant={path.role} dashed={path.role === 'reference'} />
          <span className="fm-corridor-trajectory__legend-label">{path.label}</span>
        </div>
      ))}
      {trajectory.refusals?.length ? (
        <div className="fm-corridor-trajectory__refusals" data-testid={`${testId}-refusals`}>
          {trajectory.refusalsLabel ? (
            <span className="fm-corridor-trajectory__refusals-label">
              {trajectory.refusalsLabel}
            </span>
          ) : null}
          <ul className="fm-corridor-trajectory__refusal-list">
            {groupRefusalsByReason(trajectory.refusals).map((group) =>
              group.refusals.length === 1 ? (
                <li
                  key={group.refusals[0].id}
                  className="fm-corridor-trajectory__refusal"
                  data-testid={
                    group.refusals[0].testId ?? `${testId}-refusal-${group.refusals[0].id}`
                  }
                >
                  <span className="fm-corridor-trajectory__refusal-rate">
                    {group.refusals[0].label}
                  </span>
                  {' — '}
                  <span className="fm-corridor-trajectory__refusal-reason">
                    {group.refusals[0].reason}
                  </span>
                </li>
              ) : (
                // Several rates refused for the SAME served reason: state the
                // shared reason once and list the rates under it — never the
                // identical sentence printed once per rate (visual-QA
                // round-2 n1).
                <li
                  key={group.refusals.map((refusal) => refusal.id).join('+')}
                  className="fm-corridor-trajectory__refusal fm-corridor-trajectory__refusal--grouped"
                >
                  <span className="fm-corridor-trajectory__refusal-reason">{group.reason}</span>
                  <ul className="fm-corridor-trajectory__refusal-rates">
                    {group.refusals.map((refusal) => (
                      <li
                        key={refusal.id}
                        className="fm-corridor-trajectory__refusal-rate"
                        data-testid={refusal.testId ?? `${testId}-refusal-${refusal.id}`}
                      >
                        {refusal.label}
                      </li>
                    ))}
                  </ul>
                </li>
              ),
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
